import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  InputSignal,
  model,
  ModelSignal,
  OnDestroy,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  TemplateRef,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatLabel } from '@angular/material/form-field';
import {
  ExpanderComponent,
  IconButtonComponent,
  MobileSelectionItemComponent,
  MobileSelectionWrapperComponent,
  ToolbarButtonComponent,
} from '@libs/common/components';
import { CapacitorService } from '@libs/shared/common';
import { CapabilityService } from '@libs/shared/services';
import { CategoryNodeService, ViewportService } from '@libs/shared/services';
import { ProductImpressionDirective } from '@libs/shared/tracking';
import { ShoppingListItemModel, TopLevelCategoryNode } from '@libs/shop/models';
import { ProductCategoriesFilterComponent } from '../../elements/product-categories-filter';
import { ShoppingListActionsComponent } from '../shopping-list-actions';
import { ShoppingListItemComponent, ShoppingListItemSkeletonComponent } from './shopping-list-item';

@Component({
  selector: 'ui-shopping-list-items',
  templateUrl: './shopping-list-items.component.html',
  styleUrls: ['./shopping-list-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ShoppingListItemComponent,
    MatCheckbox,
    MatLabel,
    MobileSelectionWrapperComponent,
    MobileSelectionItemComponent,
    ProductCategoriesFilterComponent,
    ExpanderComponent,
    ShoppingListActionsComponent,
    ToolbarButtonComponent,
    ShoppingListItemSkeletonComponent,
    ProductImpressionDirective,
    NgTemplateOutlet,
    IconButtonComponent,
  ],
})
export class ShoppingListItemsComponent implements AfterViewInit, OnDestroy {
  public readonly items: InputSignal<ShoppingListItemModel[]> = input.required();
  public readonly noItemsElement: InputSignal<TemplateRef<unknown> | undefined> = input();

  public readonly itemQuantityChanged: OutputEmitterRef<ShoppingListItemModel> = output();
  public readonly itemsRemoved: OutputEmitterRef<ShoppingListItemModel | ShoppingListItemModel[]> = output();
  public readonly itemsAddToCart: OutputEmitterRef<ShoppingListItemModel[]> = output();
  public readonly openAddToShoppingListQuickMobile: OutputEmitterRef<void> = output();
  public readonly openScanner: OutputEmitterRef<void> = output();
  public readonly download: OutputEmitterRef<void> = output();

  public isDownloading: InputSignal<boolean> = input(false);

  public readonly isMobile: Signal<boolean>;
  public readonly isCapacitorApp: WritableSignal<boolean> = signal(false);
  public readonly mobileSelectionVisible: WritableSignal<boolean> = signal(false);
  public readonly shoppingListDownloadEnabled: Signal<boolean> = this.capabilityService.isAvailable('shop.shoppingListDownload');

  public nodes: Signal<TopLevelCategoryNode<ShoppingListItemModel>[]> = computed(() => this.categoryNodeService.createCategoryNodes<ShoppingListItemModel>(this.items()));
  public selectedNodes: ModelSignal<TopLevelCategoryNode<ShoppingListItemModel>[]> = model([] as TopLevelCategoryNode<ShoppingListItemModel>[]);
  public visibleNodes: Signal<TopLevelCategoryNode<ShoppingListItemModel>[]> = computed(() => {
    if (!this.selectedNodes()?.length) {
      return this.nodes();
    }

    return this.selectedNodes();
  });

  public readonly selectedItemIds: WritableSignal<string[]> = signal([]);
  public readonly selectedItems: Signal<ShoppingListItemModel[]> = computed(() => this.items().filter(x => this.selectedItemIds().includes(x.id)));

  public readonly visibleItems: Signal<ShoppingListItemModel[]> = computed(() =>
    this.visibleNodes().map(x => x.children.map(y => y.items).flat()).flat());

  public readonly itemPositions: Signal<Map<ShoppingListItemModel, number>> = computed(() =>
    new Map(this.visibleItems().map((item, index) => [item, index])));

  public readonly allSelectedChecked: Signal<boolean> = computed(() =>
    this.visibleItems().length === this.selectedItems().length || this.selectedItems().length === this.items().length);

  public readonly hasSingleCategory: Signal<boolean> = computed(() => this.nodes().length === 1);

  public expandedMap: WritableSignal<Map<string, boolean>> = signal(new Map<string, boolean>([]));

  public skeletonRenderArray: Signal<number[]> = signal(new Array<number>(5).fill(0));

  private readonly actionTemplate: Signal<TemplateRef<unknown>> = viewChild.required('actions', { read: TemplateRef<unknown> });

  public constructor(
    private readonly viewportService: ViewportService,
    private readonly categoryNodeService: CategoryNodeService,
    private readonly bottomSheet: MatBottomSheet,
    private readonly capacitorService: CapacitorService,
    private readonly scrollStrategyOptions: ScrollStrategyOptions,
    private readonly capabilityService: CapabilityService,
  ) {
    this.isMobile = toSignal(this.viewportService.isMobile(), { initialValue: false });
    this.isCapacitorApp.set(this.capacitorService.isCapacitorApp());

    effect(() => {
      if (this.isMobile() && !this.mobileSelectionVisible()) {
        this.closeActions();
      }

      if (!this.isMobile() || this.mobileSelectionVisible()) {
        this.openActions();
      }
    });
  }

  public ngAfterViewInit(): void {
    /* istanbul ignore else */
    if (!this.isMobile()) {
      this.openActions();
    }
  }

  public ngOnDestroy(): void {
    this.bottomSheet.dismiss();
  }

  public toggleShift(): void {
    this.mobileSelectionVisible.set(!this.mobileSelectionVisible());
  }

  public clearOrCloseSelection(): void {
    this.mobileSelectionVisible.set(false);
    this.selectedItemIds.set([]);
  }

  public onItemQuantityChanged(item: ShoppingListItemModel): void {
    void this.itemQuantityChanged.emit(item);
  }

  public onItemRemoved(itemToRemove: ShoppingListItemModel): void {
    this.itemsRemoved.emit(itemToRemove);
  }

  public emitRemoveSelectedItems(): void {
    this.itemsRemoved.emit(this.selectedItems());
  }

  public emitAddToCartSelectedItems(): void {
    this.itemsAddToCart.emit(this.selectedItems());
  }

  public selectAll(selectAll: boolean): void {
    if (selectAll) {
      this.selectedItemIds.set(this.visibleItems().map(x => x.id));
    } else {
      this.selectedItemIds.set([]);
    }
  }

  public itemSelectionChanged(item: ShoppingListItemModel, checked: boolean): void {
    this.selectedItemIds.update((items) => {
      if (checked) {
        items.push(item.id);
      } else {
        const index: number = items.indexOf(item.id);

        items.splice(index, 1);
      }

      return items.concat();
    });
  }

  public categoryExpandedChanged(expanded: boolean, node: TopLevelCategoryNode<ShoppingListItemModel>): void {
    this.expandedMap.update(map => {
      map.set(node.label, expanded);

      return map;
    });
  }

  private openActions(): void {
    this.bottomSheet.open(this.actionTemplate(), {
      hasBackdrop: false,
      closeOnNavigation: true,
      scrollStrategy: this.scrollStrategyOptions.reposition(),
      panelClass: [
        'shopping-lists-panel-background',
      ],
    });
  }

  private closeActions(): void {
    this.bottomSheet.dismiss();
  }
}
